/**
 * ScrollPanelAtom.tsx
 *
 * A generic scroll panel atom wrapping PrimeReact's ScrollPanel component
 * with design system styling.
 */

import React, { useId } from 'react';
import { ScrollPanel, ScrollPanelProps } from 'primereact/scrollpanel';
import './ScrollPanelAtom.scss';

export type ScrollPanelVariant = 'default' | 'thin' | 'hidden';

export interface ScrollPanelAtomProps extends Omit<ScrollPanelProps, 'className'> {
  /** Visual variant for scrollbar styling */
  variant?: ScrollPanelVariant;
  /** Additional CSS class names */
  className?: string;
  /** Children content */
  children?: React.ReactNode;
}

const ScrollPanelAtom: React.FC<ScrollPanelAtomProps> = ({
  variant = 'default',
  className = '',
  children,
  ...rest
}) => {
  /**
   * PrimeReact's ScrollPanel gives both scrollbars `aria-controls="<id>_content"`
   * but never puts that id on the content element, so every panel ships a
   * reference to a node that does not exist (axe `aria-valid-attr-value`). It also
   * generates `<id>` in a MOUNT effect when no `id` prop is given, so the value
   * changes after the first paint. A stable id here plus the matching id on the
   * content element through pass-through props makes the reference resolve.
   */
  const generatedId = useId().replace(/:/g, '');
  const panelId = rest.id ?? `scroll-panel-atom-${generatedId}`;
  // Build CSS classes
  const classes = [
    'scroll-panel-atom',
    `scroll-panel-atom--variant-${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <ScrollPanel
      className={classes}
      {...rest}
      id={panelId}
      pt={{ ...rest.pt, content: { id: `${panelId}_content`, ...rest.pt?.content } }}
    >
      {children}
    </ScrollPanel>
  );
};

ScrollPanelAtom.displayName = 'ScrollPanelAtom';

export default ScrollPanelAtom;
